import { useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, PencilLine, Clock, FileSpreadsheet, MessageSquare, Paperclip, AlertTriangle, Search } from "lucide-react";
import { TaskImportDialog } from "./importConfigs";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { TASK_STATUS_COLORS, PRIORITY_COLORS, TASK_STATUSES, PRIORITIES } from "./projectUtils";
import { assigneesOf, isOverdue, hoursDiff, filterAndSortTasks } from "./taskUtils";
import TaskForm from "./TaskForm";
import TaskDetailDrawer from "./TaskDetailDrawer";
import { useConfirm } from "../ConfirmProvider";

const sel = "text-xs bg-white border border-[#E4E7EC] rounded-md px-2 h-9";

export default function TasksPanel({ project, tasks, users, canEdit, currentUser, onChanged }) {
  const confirm = useConfirm();
  const [importOpen, setImportOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [f, setF] = useState({ q: "", status: "all", priority: "all", assignee: "all", overdue: false, sort: "due" });

  const isDev = currentUser?.role === "developer";
  const nameOf = (id) => users.find(u => u.id === id)?.name || "—";
  const shown = useMemo(() => filterAndSortTasks(tasks, f), [tasks, f]);
  const assigneeOptions = useMemo(() => [...new Set(tasks.flatMap(assigneesOf))], [tasks]);

  const remove = async (id) => {
    if (!(await confirm({ title: "Are you sure you want to delete this task?", description: "This cannot be undone." }))) return;
    try { await api.delete(`/tasks/${id}`); toast.success("Deleted"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (task, status) => {
    try { await api.put(`/tasks/${task.id}`, { status }); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const canEditTask = (task) => canEdit || (isDev && assigneesOf(task).includes(currentUser.id));
  const stop = (e) => e.stopPropagation();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Tasks</div>
          <div className="text-neutral-600 text-sm">{tasks.length} tasks · {tasks.filter(t => t.status === "Completed").length} completed · <span className="text-rose-600">{tasks.filter(isOverdue).length} overdue</span></div>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="import-tasks-btn"><FileSpreadsheet className="w-4 h-4 mr-1" /> Import from Excel</Button>
            <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-task-btn" className="bg-orange-500 hover:bg-orange-600 text-white" size="sm"><Plus className="w-4 h-4 mr-1" /> New Task</Button>
          </div>
        )}
        <TaskImportDialog open={importOpen} onOpenChange={setImportOpen} projectId={project.id} onImported={onChanged} />
      </div>

      <div className="flex flex-wrap gap-2 items-center" data-testid="task-filters">
        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input value={f.q} onChange={e => setF({ ...f, q: e.target.value })} placeholder="Search by task ID or title" className="pl-9" data-testid="task-search" />
        </div>
        <select className={sel} value={f.status} onChange={e => setF({ ...f, status: e.target.value })} data-testid="task-filter-status"><option value="all">All statuses</option>{TASK_STATUSES.map(s => <option key={s}>{s}</option>)}</select>
        <select className={sel} value={f.priority} onChange={e => setF({ ...f, priority: e.target.value })} data-testid="task-filter-priority"><option value="all">All priorities</option>{PRIORITIES.map(s => <option key={s}>{s}</option>)}</select>
        <select className={sel} value={f.assignee} onChange={e => setF({ ...f, assignee: e.target.value })} data-testid="task-filter-assignee"><option value="all">All assignees</option><option value="none">Unassigned</option>{assigneeOptions.map(id => <option key={id} value={id}>{nameOf(id)}</option>)}</select>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="task-filter-overdue"><input type="checkbox" checked={f.overdue} onChange={e => setF({ ...f, overdue: e.target.checked })} /> Overdue only</label>
        <select className={`${sel} sm:ml-auto`} value={f.sort} onChange={e => setF({ ...f, sort: e.target.value })} data-testid="task-sort">
          <option value="due">Sort: Due date</option><option value="priority">Sort: Priority</option><option value="created">Sort: Newest</option><option value="updated">Sort: Last updated</option>
        </select>
      </div>

      {tasks.length === 0 ? (
        <div className="panel p-8 text-center text-neutral-500 text-sm">No tasks yet.</div>
      ) : shown.length === 0 ? (
        <div className="panel p-8 text-center text-neutral-500 text-sm" data-testid="task-no-match">No tasks match these filters.</div>
      ) : (
        <div className="space-y-2">
          {shown.map(t => {
            const overdue = isOverdue(t);
            const diff = hoursDiff(t);
            return (
              <div key={t.id} onClick={() => setDetailId(t.id)} className={`panel p-4 cursor-pointer transition-colors hover:border-orange-400 ${overdue ? "border-rose-300 bg-rose-50/40" : ""}`} data-testid={`task-row-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-mono text-neutral-500">{t.task_id}</span>
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${PRIORITY_COLORS[t.priority] || ""}`}>{t.priority}</span>
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${TASK_STATUS_COLORS[t.status] || ""}`}>{t.status}</span>
                      {overdue && <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-rose-50 border-rose-300 text-rose-700 flex items-center gap-1" data-testid={`task-overdue-${t.id}`}><AlertTriangle className="w-3 h-3" /> Overdue</span>}
                    </div>
                    <div className="font-semibold mt-1">{t.name}</div>
                    {t.description && <div className="text-xs text-neutral-600 mt-1 line-clamp-2">{t.description}</div>}
                    <div className="mt-2 flex items-center gap-3 text-[10px] font-mono text-neutral-500 flex-wrap">
                      <span>Assignees: <span className="text-neutral-700">{assigneesOf(t).map(nameOf).join(", ") || "—"}</span></span>
                      {t.due_date && <span className={overdue ? "text-rose-600 font-semibold" : ""}>Due: {t.due_date}</span>}
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t.actual_hours || 0}h / {t.estimated_hours || 0}h{diff !== 0 && <span className={diff > 0 ? "text-rose-600" : "text-emerald-700"}>({diff > 0 ? "+" : ""}{diff}h)</span>}</span>
                      <span className="flex items-center gap-1" data-testid={`task-comment-count-${t.id}`}><MessageSquare className="w-3 h-3" /> {t.comments?.length || 0}</span>
                      <span className="flex items-center gap-1" data-testid={`task-att-count-${t.id}`}><Paperclip className="w-3 h-3" /> {t.attachments?.length || 0}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0" onClick={stop}>
                    {canEditTask(t) && (
                      <select className="text-[11px] font-mono bg-[#FFFFFF] border border-[#E4E7EC] rounded px-2 py-1" value={t.status} onChange={e => setStatus(t, e.target.value)} data-testid={`task-status-select-${t.id}`}>
                        {TASK_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )}
                    {canEdit && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => { setEdit(t); setOpen(true); }} data-testid={`task-edit-${t.id}`}><PencilLine className="w-4 h-4 text-neutral-600" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(t.id)} data-testid={`task-delete-${t.id}`}><Trash2 className="w-4 h-4 text-rose-400" /></Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TaskForm open={open} onOpenChange={setOpen} initial={edit} projectId={project.id} project={project} users={users} onSaved={onChanged} />
      <TaskDetailDrawer taskId={detailId} open={!!detailId} onOpenChange={(o) => { if (!o) setDetailId(null); }} users={users} project={project} currentUser={currentUser} canManage={canEdit} onChanged={onChanged} />
    </div>
  );
}
